import { useEffect, useState } from 'react';
import { CalendarCheck, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { DIGEST_TABS, DigestStat, DigestRow, filterDigest } from '../components/BadgeDigestParts';

const GENDERS = [
  { id: 'all', label: 'الجميع' },
  { id: 'male', label: 'القرّاء' },
  { id: 'female', label: 'القارئات' },
];

export default function AdminBadgeDigestPage() {
  const [date, setDate] = useState(() => new Date(Date.now() - 864e5).toLocaleDateString('en-CA', { timeZone: 'Asia/Muscat' }));
  const [gender, setGender] = useState('all');
  const [tab, setTab] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get('/admin/badges/daily-digest', { params: { date, gender } })
      .then((res) => setData(res.data))
      .catch((err) => toast.error(err.response?.data?.detail || 'فشل تحميل الملخص'))
      .finally(() => setLoading(false));
  }, [date, gender]);

  const items = data ? filterDigest(data.items, tab, data.near_days) : [];
  const activeTab = DIGEST_TABS.find((t) => t.id === tab);

  return (
    <div className="space-y-6" data-testid="admin-badge-digest-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-accent flex items-center gap-2">
            <CalendarCheck className="w-7 h-7 text-amber-600" /> الملخص اليومي لأوسمة الاستمرار
          </h1>
          <p className="text-muted-foreground text-sm mt-2">حركة عدّادات الاستمرار لمن بلغ القمة في اليوم المحدد (افتراضياً: أمس).</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-xl border-2 border-amber-200 overflow-hidden" data-testid="digest-gender-filter">
            {GENDERS.map((g) => (
              <button key={g.id} type="button" onClick={() => setGender(g.id)}
                className={`px-4 h-10 text-sm font-semibold transition-colors ${gender === g.id ? 'bg-amber-600 text-white' : 'bg-white text-amber-800 hover:bg-amber-50'}`}
                data-testid={`digest-gender-${g.id}`}>{g.label}</button>
            ))}
          </div>
          <input type="date" value={date} max={new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Muscat' })}
            onChange={(e) => setDate(e.target.value)}
            className="h-10 rounded-xl border-2 border-amber-200 px-3 text-sm bg-white focus:outline-none focus:border-amber-500"
            data-testid="digest-date-input" />
        </div>
      </div>

      {data?.is_friday && (
        <div className="flex items-center gap-2 text-sm rounded-xl border border-blue-200 bg-blue-50 text-blue-800 p-3" data-testid="digest-friday-note">
          <Info className="w-4 h-4 shrink-0" /> هذا اليوم جمعة (إجازة في نظام القلاع)، لذلك التوقف فيه طبيعي.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {DIGEST_TABS.map((t) => (
          <DigestStat key={t.id} tab={t} value={data?.summary?.[t.key]} active={tab === t.id} onClick={() => setTab(t.id)} />
        ))}
      </div>

      <Card className="overflow-hidden" data-testid="digest-list">
        <div className="px-4 py-3 border-b bg-amber-50/50 flex items-center justify-between">
          <h3 className="font-bold text-accent">{activeTab?.label} {data && `— ${data.date}`}</h3>
          <span className="text-xs text-muted-foreground">{items.length} نتيجة</span>
        </div>
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></div>
        ) : items.length === 0 ? (
          <p className="p-10 text-center text-muted-foreground" data-testid="digest-empty">لا يوجد قرّاء في هذه الفئة لهذا اليوم.</p>
        ) : (
          items.map((i) => <DigestRow key={i.reader_number} item={i} />)
        )}
      </Card>
    </div>
  );
}
